// CCC.1 + CCC.3 — Panneau admin "Outils de test E2E"
// Permet à l'admin de :
//   1. Activer une mission gratuitement pour un user (bypass Stripe)
//   2. Vider toutes les missions/abonnements/audits d'un user (reset propre)
//
// Le bouton "Simuler Succès Paiement" côté client a été retiré (CCC.1) car il
// créait un conflit entre le flow Stripe et le flow bypass. Désormais tout
// passe par cet outil admin.

import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FlaskConical, Trash2, ArrowRight, AlertTriangle, CheckCircle, User as UserIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../App';

const PACK_OPTIONS = [
  { value: 'mission_demenagement', label: 'Mission Déménagement', color: 'cyan' },
  { value: 'mission_heritage', label: 'Mission Héritage', color: 'magenta' },
  { value: 'mission_audit', label: 'Mission Audit', color: 'success' },
];

const E2ETestPanel = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPack, setSelectedPack] = useState('mission_demenagement');
  const [activating, setActivating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [lastAction, setLastAction] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    const token = localStorage.getItem('token');
    // CCC.13 — fix : endpoint correct = /admin-portal/users (pas /admin/users)
    const endpoints = [`${API}/admin-portal/users`, `${API}/admin/users`];
    for (const url of endpoints) {
      try {
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const raw = res.data;
        const list = Array.isArray(raw)
          ? raw
          : (raw.users || raw.results || raw.data || []);
        if (Array.isArray(list) && list.length > 0) {
          // Normalise : prend user_id ou _id ou id
          const normalized = list.map(u => ({
            user_id: u.user_id || u._id || u.id,
            email: u.email,
            is_admin: u.is_admin || false,
          })).filter(u => u.user_id && u.email);
          setUsers(normalized);
          // Sélectionne le current admin par défaut
          const myEmail = (localStorage.getItem('userEmail') || '').toLowerCase();
          const me = normalized.find(u => (u.email || '').toLowerCase() === myEmail);
          if (me) setSelectedUserId(me.user_id);
          else if (normalized[0]) setSelectedUserId(normalized[0].user_id);
          return;
        }
      } catch (e) {
        // try next endpoint
      }
    }
    // Fallback ultime : si aucun endpoint ne marche, prend le current user via /auth/me
    try {
      const me = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const u = me.data.user || me.data;
      if (u?.user_id) {
        setUsers([{ user_id: u.user_id, email: u.email, is_admin: true }]);
        setSelectedUserId(u.user_id);
      }
    } catch {}
  };

  // Active 1 pack OU les 3 selon le param `all`
  const activatePack = async (packType, targetUserId, token) => {
    const body = { pack_type: packType };
    if (targetUserId && targetUserId !== 'self') body.target_user_id = targetUserId;
    return axios.post(`${API}/payments/admin-bypass`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const handleActivateMission = async (mode = 'single') => {
    if (!selectedUserId) {
      toast.error('Sélectionne un utilisateur');
      return;
    }
    setActivating(true);
    try {
      const token = localStorage.getItem('token');
      if (mode === 'all') {
        // CCC.11 : active les 3 packs en parallèle pour tester E2E sur 1 user
        const results = await Promise.allSettled([
          activatePack('mission_demenagement', selectedUserId, token),
          activatePack('mission_heritage', selectedUserId, token),
          activatePack('mission_audit', selectedUserId, token),
        ]);
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const ko = results.length - ok;
        if (ok === 3) {
          toast.success('Les 3 packs activés (Déménagement + Héritage + Audit)');
        } else {
          toast.warning(`${ok}/3 packs activés (${ko} échec(s))`);
        }
        setLastAction({
          type: 'activated_all',
          ok, ko,
          details: results.map((r, i) => ({
            pack: ['Déménagement', 'Héritage', 'Audit'][i],
            ok: r.status === 'fulfilled',
            error: r.status === 'rejected' ? (r.reason?.response?.data?.detail || r.reason?.message) : null,
          })),
        });
      } else {
        const res = await activatePack(selectedPack, selectedUserId, token);
        if (res.data.status === 'success') {
          toast.success(`${res.data.pack_name} activée`);
          setLastAction({
            type: 'activated',
            pack: res.data.pack_name,
            mission_id: res.data.mission_id,
          });
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur activation');
      console.error('admin-bypass error:', e);
    } finally {
      setActivating(false);
    }
  };

  const handleClearMissions = async () => {
    if (!selectedUserId) {
      toast.error('Sélectionne un utilisateur');
      return;
    }
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 5000);
      return;
    }
    setClearing(true);
    try {
      const token = localStorage.getItem('token');
      // CCC.11 : si 'self' (admin teste sur soi-même), on prend le user_id réel
      let targetId = selectedUserId;
      if (targetId === 'self') {
        try {
          const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          targetId = me.data.user?.user_id || me.data.user_id;
        } catch (e) {
          toast.error('Impossible de récupérer ton user_id');
          setClearing(false);
          return;
        }
      }
      const res = await axios.delete(
        `${API}/admin-portal/users/${targetId}/clear-missions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.total_deleted} entrées supprimées pour ${res.data.user_email}`);
      setLastAction({
        type: 'cleared',
        email: res.data.user_email,
        deleted: res.data.deleted,
        total: res.data.total_deleted,
      });
      setConfirmClear(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur reset');
      console.error('clear-missions error:', e);
    } finally {
      setClearing(false);
    }
  };

  const selectedUser = users.find(u => u.user_id === selectedUserId);
  // DDD.10 : panneau collapsable — replié par défaut pour libérer l'espace de la page
  // admin et ne PLUS être visible en permanence (c'est un outil de dev, pas opérationnel).
  const [e2ePanelOpen, setE2ePanelOpen] = useState(false);

  return (
    <div className="glass-card border border-orange-500/30 bg-orange-500/5 rounded-xl mb-6 overflow-hidden">
      <button
        type="button"
        onClick={() => setE2ePanelOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-orange-500/10 transition-colors"
        data-testid="e2e-panel-toggle"
      >
        <div className="flex items-center gap-3">
          <FlaskConical className="w-5 h-5 text-orange-400 flex-shrink-0" />
          <div className="text-left">
            <h3 className="font-orbitron text-sm font-bold text-orange-300">Outils de test E2E (admin)</h3>
            <p className="text-[11px] text-gray-500">{e2ePanelOpen ? 'Masquer' : 'Afficher'} — active une mission sans Stripe ou reset les missions d\'un user</p>
          </div>
        </div>
        <span className={`text-orange-300 text-lg transition-transform ${e2ePanelOpen ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {!e2ePanelOpen ? null : (
      <div className="p-6 pt-2 border-t border-orange-500/20">

      {/* Sélecteur user + pack */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-mono text-gray-500 mb-1 uppercase">Utilisateur</label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="w-full bg-gray-900/80 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
          >
            <option value="">— Choisir un utilisateur —</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.email} {u.is_admin ? '(admin)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono text-gray-500 mb-1 uppercase">Pack</label>
          <select
            value={selectedPack}
            onChange={(e) => setSelectedPack(e.target.value)}
            className="w-full bg-gray-900/80 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
          >
            {PACK_OPTIONS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => handleActivateMission('single')}
          disabled={activating || !selectedUserId}
          className="bg-success hover:bg-success/90 text-black font-bold flex items-center gap-2"
        >
          {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          Activer ce pack
        </Button>

        <Button
          onClick={() => handleActivateMission('all')}
          disabled={activating || !selectedUserId}
          className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold flex items-center gap-2"
        >
          {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          Activer les 3 packs
          <ArrowRight className="w-4 h-4" />
        </Button>

        <Button
          onClick={handleClearMissions}
          disabled={clearing || !selectedUserId}
          variant="outline"
          className={`border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-2 ml-auto ${confirmClear ? 'bg-red-500/20 border-red-500' : ''}`}
        >
          {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {confirmClear ? 'Confirmer reset (5s)' : 'Reset missions'}
        </Button>
      </div>

      {/* Disclaimer */}
      {selectedUserId && (
        <div className="mt-3 text-[11px] text-gray-500 font-mono flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 text-yellow-500 flex-shrink-0 mt-0.5" />
          <span>
            <strong>Activer les 3 packs</strong> crée Déménagement + Héritage + Audit en parallèle pour ce user (test E2E sur 1 seul compte).
            <strong className="ml-2">Reset</strong> supprime missions, abonnements, mandats, audits, documents, tickets pour ce user — le compte est conservé.
          </span>
        </div>
      )}

      {/* Résultat dernière action */}
      {lastAction && (
        <div className="mt-4 p-3 bg-gray-900/50 border border-gray-800 rounded-lg text-xs font-mono">
          {lastAction.type === 'activated' && (
            <>
              <span className="text-success font-bold">✓ ACTIVÉ</span>
              <span className="text-gray-400 ml-2">
                {lastAction.pack} · mission_id: {lastAction.mission_id?.slice(0, 16)}...
              </span>
            </>
          )}
          {lastAction.type === 'activated_all' && (
            <>
              <span className={lastAction.ko === 0 ? 'text-success font-bold' : 'text-yellow-400 font-bold'}>
                {lastAction.ok}/3 PACKS ACTIVÉS
              </span>
              <div className="mt-2 space-y-1">
                {lastAction.details.map((d, i) => (
                  <div key={i} className="text-gray-400">
                    <span className={d.ok ? 'text-success' : 'text-red-400'}>{d.ok ? '✓' : '✗'}</span>
                    <span className="ml-2">{d.pack}</span>
                    {!d.ok && <span className="ml-2 text-red-400/80">— {d.error}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
          {lastAction.type === 'cleared' && (
            <>
              <span className="text-red-400 font-bold">✓ RESET</span>
              <span className="text-gray-400 ml-2">
                {lastAction.email} · {lastAction.total} entrées supprimées
              </span>
              {lastAction.deleted && Object.keys(lastAction.deleted).length > 0 && (
                <div className="mt-2 text-gray-500">
                  {Object.entries(lastAction.deleted).map(([k, v]) => (
                    <span key={k} className="inline-block mr-3">
                      {k}: <span className="text-cyan-400">{v}</span>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>
      )}
    </div>
  );
};

export default E2ETestPanel;
